import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { api, getToken, onSessionChange, refresh, refreshSession, setSession, type SessionUser } from './api';

export interface Me {
  id: string;
  email: string;
  name: string;
  roles: string[];
  timezone: string;
  reportingCurrency: string;
  quietHours: { enabled: boolean; start: string | null; end: string | null };
  telegramLinked: boolean;
  totpEnabled: boolean;
  accounts: { id: string; code: string; name: string; currency: string; market: { id: string; code: string; name: string } }[];
  symbols: { id: string; symbol: string; displayName: string; marketId: string; marketCode: string }[];
}

/** 'offline' = we couldn't reach the server to check the session (not the same as signed out). */
type State = { status: 'loading' | 'out' | 'in' | 'offline'; user: SessionUser | null };
const Ctx = createContext<{ state: State; signOut: () => Promise<void> }>({ state: { status: 'loading', user: null }, signOut: async () => undefined });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading', user: null });
  const qc = useQueryClient();

  useEffect(() => {
    onSessionChange((user) => setState(user ? { status: 'in', user } : { status: 'out', user: null }));
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () =>
      void refreshSession().then((r) => {
        if (r === 'denied') setState({ status: 'out', user: null });
        if (r === 'offline') {
          setState((s) => (s.status === 'in' ? s : { status: 'offline', user: null }));
          timer = setTimeout(check, 15_000); // keep trying; 'online' below retries at once
        }
      });
    const online = () => {
      clearTimeout(timer);
      check();
    };
    window.addEventListener('online', online);
    check();
    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', online);
    };
  }, []);

  // live updates: socket events are "refetch" hints (REST stays the source of truth)
  useEffect(() => {
    if (state.status !== 'in') return;
    const socket: Socket = io({ path: '/api/v1/socket', auth: (cb) => cb({ token: getToken() }), transports: ['websocket', 'polling'] });
    const inv = (...keys: string[]) => keys.forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
    socket.on('notification', () => inv('notifications', 'today', 'handovers', 'setup'));
    socket.on('feed.item', () => inv('feed', 'today', 'setups', 'setup'));
    socket.on('due.changed', () => inv('today', 'risk', 'trade', 'setups', 'setup', 'handovers'));
    socket.on('approval.changed', () => inv('risk', 'plan', 'today'));
    socket.on('trade.changed', () => inv('trades', 'trade', 'today', 'risk'));
    socket.on('social.changed', () => inv('social', 'feed'));
    socket.on('webinars.changed', () => inv('webinars', 'today'));
    socket.on('rooms.changed', () => inv('rooms'));
    socket.on('connect_error', () => void refresh());
    return () => {
      socket.disconnect();
    };
  }, [state.status, qc]);

  const signOut = async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    setSession(null, null);
    qc.clear();
  };

  return <Ctx.Provider value={{ state, signOut }}>{children}</Ctx.Provider>;
}

export const useSession = () => useContext(Ctx);

export function useMe() {
  const { state } = useSession();
  return useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me'), enabled: state.status === 'in', staleTime: 60_000 });
}

export const isStaff = (roles: string[] | undefined) => !!roles?.some((r) => r === 'ADMIN' || r === 'MASTER_TRADER' || r === 'RISK_MANAGER');
export const hasRole = (roles: string[] | undefined, ...want: string[]) => !!roles?.some((r) => want.includes(r));
